import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { agentTenderService } from "../../services/agentTenderApi";

// Posting-side responses received on the agency's own tenders. Loads the full
// set once and lets the page slice it per tab (Responses = active/un-actioned,
// Shortlist = shortlisted, Declined = rejected). Accept/Reject/Shortlist mutate
// in place.
export const useAgentTenderResponses = () => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchResponses = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await agentTenderService.getResponses();
    if (res.success) setResponses(res.data);
    else setError(res.error || "Failed to load responses.");
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  const patchLocal = (id, patch) =>
    setResponses((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const accept = async (response) => {
    setBusyId(response.id);
    const res = await agentTenderService.setResponseStatus(response.id, "accepted");
    setBusyId(null);
    if (res.success) {
      patchLocal(response.id, {
        status: "accepted",
        // Accept returns { channel: { channel_id } } for the chat deep-link.
        channel_id: res.data?.channel?.channel_id ?? res.data?.channel_id ?? response.channel_id,
      });
      toast.success("Response accepted. A chat with the agent is now open.");
      return true;
    }
    // Accept opens the chat channel first; a 503 means the channel failed and
    // the status did not change (backend contract). Surface it, don't fake it.
    if (res.status === 503) {
      toast.error("We couldn't open the chat right now. The response was not accepted. Please try again.");
    } else {
      toast.error(res.error || "We couldn't accept this response. Please try again.");
    }
    return false;
  };

  const reject = async (response) => {
    setBusyId(response.id);
    const res = await agentTenderService.setResponseStatus(response.id, "rejected");
    setBusyId(null);
    if (res.success) {
      patchLocal(response.id, { status: "rejected" });
      toast.success("Response declined.");
      return true;
    }
    toast.error(res.error || "We couldn't decline this response. Please try again.");
    return false;
  };

  const toggleShortlist = async (response) => {
    const next = !response.is_shortlisted;
    setBusyId(response.id);
    const res = await agentTenderService.setResponseShortlist(response.id, next);
    setBusyId(null);
    if (res.success) {
      patchLocal(response.id, { is_shortlisted: next });
      toast.success(next ? "Added to shortlist." : "Removed from shortlist.");
      return true;
    }
    toast.error(res.error || "We couldn't update the shortlist. Please try again.");
    return false;
  };

  return {
    responses,
    loading,
    error,
    busyId,
    fetchResponses,
    accept,
    reject,
    toggleShortlist,
  };
};
